import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  TemplateRef,
  AfterViewInit, // Import AfterViewInit for sort/paginator
} from "@angular/core";
import {
  MatPaginator,
  MatTableDataSource,
  MatDialog,
  MatSort,
  PageEvent,
} from "@angular/material";
import { HttpService } from "../http.service";
import { Subscription, Subject } from "rxjs";
import { EventService } from "../event.service";
import { StorageService } from "../storage.service";
import { Router } from "@angular/router";
import { ConfirmationModalComponent } from "../confirmation-modal/confirmation-modal.component";
import { Messages } from "../constants/messages";
import { BulkUploadComponent } from "../bulk-upload/bulk-upload.component";
import { environment } from "src/environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "../snackbar.service";
import { ValidationService } from "../validation.service";
import { TimeService } from "../time.service";
import { BulkUploadVehicleComponent } from "../bulk-upload-vehicle/bulk-upload-vehicle.component";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { MatDatepickerInputEvent } from "@angular/material/datepicker"; // Import this

@Component({
  selector: "app-vehicles-dashboard",
  templateUrl: "./vehicles-dashboard.component.html",
  styleUrls: ["./vehicles-dashboard.component.scss"],
})
export class VehiclesDashboardComponent implements OnInit, AfterViewInit {
  showLoader = false;

  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  // Remove static: true if these are conditionally rendered (e.g., inside *ngIf)
  @ViewChild("uploadDialog", { static: false }) uploadDialog: TemplateRef<any>;
  @ViewChild("markMaintainance", { static: false }) markMaintainance: TemplateRef<any>;
  @ViewChild("dedicateVehicleTo", { static: false }) dedicateVehicleTo: TemplateRef<any>;
  @ViewChild("openVideos", { static: false }) openVideos: TemplateRef<any>;

  displayedColumns: string[] = [
    "sno",
    "date",
    "pickup",
    "destination",
    "doNumber",
    "invoiceNumber",
    "freightRate",
  ];
  dataSource: MatTableDataSource<any> = new MatTableDataSource([]); // Initialize
  shipmentRecords: any[] = [];
  pageSizeOptions: number[] = [5, 10, 25, 100];
  pageSize: number = 10;
  filterValue: string = ""; // If search is re-enabled

  // --- Dashboard Card Properties ---
  totalFreightValue: number = 0;
  averageFreightRate: number = 0;
  totalVehicles: number = 0;

  // --- Date Properties ---
  // Use Date objects for easier logic. Initialize directly.
  // Calculate initial dates without helper initially, as helpers are methods of the class
  fromDate: Date | null = new Date(new Date().setDate(new Date().getDate() - 3));
  toDate: Date | null = new Date();
  today: Date; // Will be initialized in ngOnInit after normalization

  constructor(
    private dialog: MatDialog,
    private events: EventService, // Consider removing if unused
    private storage: StorageService,
    private route: Router,
    private http: HttpService,
    private changeDetection: ChangeDetectorRef, // May not be needed if using standard change detection
    private translation: TranslateService, // Consider removing if unused
    private snackbar: SnackbarService,
    private valid: ValidationService, // Consider removing if unused
    private timeserive: TimeService, // Consider removing if unused
    private sanitizer: DomSanitizer // Consider removing if unused
  ) {
    const access_token = this.storage.getFromCookie("access_token");
    if (!access_token) {
      this.route.navigateByUrl("login");
    }
    // Initialize today here or in ngOnInit - ngOnInit is generally safer
    // this.today = this.normalizeDateUTC(new Date());
  }

  ngOnInit() {
    // Normalize initial dates and today
    this.today = this.normalizeDateUTC(new Date());
    this.fromDate = this.fromDate ? this.normalizeDateUTC(this.fromDate) : null;
    this.toDate = this.toDate ? this.normalizeDateUTC(this.toDate) : null;

    // Perform initial validation/adjustment in case defaults are invalid
    this.validateAndAdjustDatesOnInit();

    this.getVehicles(); // Fetch initial data
  }

  ngAfterViewInit() {
    // Assign paginator and sort after they are initialized in the view
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  // --- Date Helper Functions ---
  private normalizeDateUTC(date: Date): Date {
    // Creates a new Date object representing the UTC midnight of the input date
    // This avoids timezone issues when comparing dates or adding/subtracting days.
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  }

  private addDays(date: Date, days: number): Date {
    const newDate = new Date(date.getTime()); // Clone date
    newDate.setUTCDate(newDate.getUTCDate() + days); // Use UTC date for consistency with normalizeDateUTC
    return newDate;
  }

  // --- Initial Date Validation (Called in ngOnInit) ---
  private validateAndAdjustDatesOnInit() {
    // Ensure initial dates adhere to rules (today max, range <= 3 days, from <= to)
    if (this.fromDate && this.normalizeDateUTC(this.fromDate) > this.today) {
      this.fromDate = new Date(this.today);
    }
    if (this.toDate && this.normalizeDateUTC(this.toDate) > this.today) {
      this.toDate = new Date(this.today);
    }
    if (this.fromDate && this.toDate) {
      const fromNorm = this.normalizeDateUTC(this.fromDate);
      const toNorm = this.normalizeDateUTC(this.toDate);

      if (fromNorm > toNorm) {
        // If fromDate is after toDate initially, set fromDate = toDate
        this.fromDate = new Date(this.toDate);
      } else {
        // Check range only if order is correct
        const maxAllowedToDate = this.addDays(this.fromDate, 3);
        if (toNorm > this.normalizeDateUTC(maxAllowedToDate)) {
          this.toDate = maxAllowedToDate;
          // Re-check if capping toDate made it go past today
          if (this.normalizeDateUTC(this.toDate) > this.today) {
            this.toDate = new Date(this.today);
            // If fromDate was also today, ensure from isn't now after to
            if (this.normalizeDateUTC(this.fromDate) > this.normalizeDateUTC(this.toDate)) {
              this.fromDate = new Date(this.toDate);
            }
          }
        }
      }
    } else if (this.fromDate && !this.toDate) {
      // If only fromDate exists, set toDate based on it
      this.toDate = this.addDays(this.fromDate, 3);
      if (this.normalizeDateUTC(this.toDate) > this.today) {
        this.toDate = new Date(this.today);
      }
    } else if (!this.fromDate && this.toDate) {
      // If only toDate exists, set fromDate based on it
      this.fromDate = this.addDays(this.toDate, -3);
    }
    // If both were null, they remain null until selected or default applied elsewhere.
  }

  // --- Datepicker Event Handlers ---
  fromDateHandler(event: MatDatepickerInputEvent<Date | null>) {
    if (!event || !event.value || isNaN(new Date(event.value).getTime())) return;
    let newFromDate = this.normalizeDateUTC(new Date(event.value));

    // Constraint: fromDate cannot be after today
    if (newFromDate > this.today) {
      newFromDate = new Date(this.today); // Cap at today (already normalized)
    }

    // Tentatively update fromDate
    const potentialFromDate = new Date(newFromDate);

    // Get current toDate for adjustments
    let currentToDate = this.toDate ? this.normalizeDateUTC(this.toDate) : null;
    let adjustedToDate = currentToDate ? new Date(currentToDate) : null; // Create mutable copy
    let toDateWasAdjusted = false;

    if (adjustedToDate) {
      // Constraint: fromDate cannot be after toDate
      if (potentialFromDate > adjustedToDate) {
        adjustedToDate = new Date(potentialFromDate);
        toDateWasAdjusted = true;
      }

      // Constraint: Difference cannot be more than 3 days (toDate - fromDate <= 3)
      const maxAllowedToDate = this.addDays(potentialFromDate, 3);
      if (adjustedToDate > maxAllowedToDate) {
        adjustedToDate = maxAllowedToDate;
        toDateWasAdjusted = true;
      }

      // Constraint: Adjusted toDate cannot be after today
      if (adjustedToDate > this.today) {
        adjustedToDate = new Date(this.today);
        // Re-check if capping made it earlier than fromDate (if fromDate is today)
        if (adjustedToDate < potentialFromDate) {
          adjustedToDate = new Date(potentialFromDate);
        }
        toDateWasAdjusted = true;
      }
    } else {
      // If toDate was null, set a default based on the new fromDate
      adjustedToDate = this.addDays(potentialFromDate, 3);
      if (adjustedToDate > this.today) {
        adjustedToDate = new Date(this.today);
      }
      toDateWasAdjusted = true;
    }

    // --- Final Updates ---
    this.fromDate = potentialFromDate; // Commit the final fromDate
    if (toDateWasAdjusted && adjustedToDate) {
      this.toDate = adjustedToDate; // Commit the adjusted toDate
    }
    this.getVehicles();
  }

  toDateHandler(event: MatDatepickerInputEvent<Date | null>) {
    if (!event || !event.value || isNaN(new Date(event.value).getTime())) return;
    let newToDate = this.normalizeDateUTC(new Date(event.value)); // Normalize input

    // Constraint: toDate cannot be after today
    if (newToDate > this.today) {
      newToDate = new Date(this.today); // Cap at today (already normalized)
    }

    // Tentatively update toDate
    const potentialToDate = new Date(newToDate);

    // Get current fromDate for adjustments
    let currentFromDate = this.fromDate ? this.normalizeDateUTC(this.fromDate) : null;
    let adjustedFromDate = currentFromDate ? new Date(currentFromDate) : null; // Mutable copy
    let fromDateWasAdjusted = false;

    if (adjustedFromDate) {
      // Constraint: toDate cannot be before fromDate
      if (potentialToDate < adjustedFromDate) {
        adjustedFromDate = new Date(potentialToDate);
        fromDateWasAdjusted = true;
      }

      // Constraint: Difference cannot be more than 3 days (toDate - fromDate <= 3)
      const minAllowedFromDate = this.addDays(potentialToDate, -3);
      if (adjustedFromDate < minAllowedFromDate) {
        adjustedFromDate = minAllowedFromDate;
        fromDateWasAdjusted = true;
      }
      // No 'today' constraint check for fromDate adjustment based on toDate change.
    } else {
      // If fromDate was null, set a default based on the new toDate
      adjustedFromDate = this.addDays(potentialToDate, -3);
      fromDateWasAdjusted = true;
    }

    // --- Final Updates ---
    this.toDate = potentialToDate; // Commit the final toDate
    if (fromDateWasAdjusted && adjustedFromDate) {
      this.fromDate = adjustedFromDate; // Commit the adjusted fromDate
    }
    this.getVehicles();
  }

  // --- Data Fetching ---
  getVehicles() {
    // Ensure dates are Date objects before formatting
    const fromDateObj = this.fromDate instanceof Date ? this.fromDate : null;
    const toDateObj = this.toDate instanceof Date ? this.toDate : null;

    if (!fromDateObj || !toDateObj) {
      console.warn("From date or To date is missing or invalid. Skipping API call.");
      this.dataSource.data = []; // Clear table data
      this.totalVehicles = 0;
      this.totalFreightValue = 0;
      this.averageFreightRate = 0;
      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage(); // Reset paginator if data cleared
      }
      return;
    }

    // Format dates as YYYY-MM-DD string just before API call
    // Use UTC methods to get parts to avoid timezone shifting the date
    const formatToIsoDateString = (date: Date): string => {
      const year = date.getUTCFullYear();
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // months are 0-indexed
      const day = date.getUTCDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const fromParam = formatToIsoDateString(fromDateObj);
    const toParam = formatToIsoDateString(toDateObj);

    this.showLoader = true;
    this.http.get(`shipment/jspInvoiceDashboard?from=${fromParam}&to=${toParam}`)
      .subscribe(
        (res: any) => {
          if (res.statusCode == 200) {
            const data = res.data || []; // Ensure data is an array
            this.totalVehicles = data.length;
            this.totalFreightValue = data.reduce((sum, item) => sum + (item.freight_rate || 0), 0);
            this.averageFreightRate = this.totalVehicles > 0 ? Math.round((this.totalFreightValue / this.totalVehicles) * 100) / 100 : 0;

            this.shipmentRecords = data.map((item, index) => ({
              sno: index + 1,
              date: item.date ? new Date(item.date).toLocaleDateString() : '-', // Format date for display
              pickup: item.p && item.p.length > 0 ? item.p[0] : '-',
              destination: item.d && item.d.length > 0 ? item.d[0] : '-',
              doNumber: item.do_number || '-',
              invoiceNumber: item.invoice_num || '-',
              freightRate: item.freight_rate || 0
            }));

            this.dataSource.data = this.shipmentRecords;
            // Re-apply sort/paginator if needed after data update
            if (this.dataSource.paginator) {
              this.dataSource.paginator.firstPage(); // Go to first page on new data
            }
            if (this.sort) { // Apply current sort state
              this.dataSource.sort = this.sort;
            }

          } else {
            this.snackbar.openSnackBar(res.message || 'Error fetching data', '', 'err');
            this.clearTableData(); // Clear data on error
          }
          this.showLoader = false;
        },
        (error) => {
          console.error("API Error:", error);
          this.snackbar.openSnackBar('Failed to fetch shipment data. Please try again.', '', 'err');
          this.clearTableData(); // Clear data on HTTP error
          this.showLoader = false;
        }
      );
  }

  // --- Utility Methods ---
  applyFilter(event: Event) { // Changed to accept Event for better practice
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  handlePageEvent(event: PageEvent) {
    // This function is called by the paginator, no need to manually set pageSize here
    // The paginator component handles updating its state.
    // You might use this if you need server-side pagination.
    this.pageSize = event.pageSize;
  }

  private clearTableData() {
    this.dataSource.data = [];
    this.shipmentRecords = [];
    this.totalVehicles = 0;
    this.totalFreightValue = 0;
    this.averageFreightRate = 0;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  // --- Other Placeholder Methods (from original code) ---
  // Implement or remove these based on actual usage
  openUploadDialog() {
    // Example: this.dialog.open(this.uploadDialog);
    // Example: this.dialog.open(BulkUploadComponent); // If it's a component
    this.dialog.open(BulkUploadVehicleComponent, { // Assuming this is the correct component
      width: '500px' // Example width
    });
  }

  openMarkMaintenanceDialog(vehicle: any) {
    // Example: this.dialog.open(this.markMaintainance, { data: vehicle });
    this.dialog.open(ConfirmationModalComponent, { // Example using confirmation
      data: { message: `Mark vehicle ${vehicle.vehicleNumber || ''} for maintenance?` }
    }).afterClosed().subscribe(result => {
      if (result) { /* Handle confirmation */ }
    });
  }

  openDedicateVehicleDialog(vehicle: any) {
    // Example: this.dialog.open(this.dedicateVehicleTo, { data: vehicle });
  }

  openVideosDialog(videoUrl: string) {
    // Example: this.dialog.open(this.openVideos, { data: { url: videoUrl } });
  }

  applyFilterField(inputValue: string) {
    const filterValue = inputValue.trim().toLowerCase();
    this.dataSource.filter = filterValue; // Assign the string to the filter property

    // Reset pagination to the first page when filter changes
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }
}